import { useSyncExternalStore } from "react";

// Per-person chat list choices, kept in this browser: pinned chats (shown first), muted chats (no sound or banner, left out of the
// unread total) and chats marked as unread. Keys are the list keys, "dm:<id>" and "group:<id>".

export interface ChatPrefs {
    pinned: string[];
    muted: string[];
    unread: string[];
    /** Hidden from the list until you search for them or they are unhidden. */
    hidden: string[];
}

const EMPTY: ChatPrefs = { pinned: [], muted: [], unread: [], hidden: [] };
const listeners = new Set<() => void>();
const cache = new Map<string, ChatPrefs>();

const storageKey = (userId: string) => `fg.connect.chatPrefs:${userId || "anon"}`;

export function readChatPrefs(userId: string): ChatPrefs {
    const hit = cache.get(userId);
    if (hit) return hit;
    let prefs = EMPTY;
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (raw) {
            const j = JSON.parse(raw) as Partial<ChatPrefs>;
            const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
            prefs = { pinned: list(j.pinned), muted: list(j.muted), unread: list(j.unread), hidden: list(j.hidden) };
        }
    } catch {
        /* private mode or a damaged value: start empty */
    }
    cache.set(userId, prefs);
    return prefs;
}

function write(userId: string, next: ChatPrefs) {
    cache.set(userId, next);
    try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(next));
    } catch {
        /* the choice still holds for this session */
    }
    listeners.forEach((l) => l());
}

type Which = keyof ChatPrefs;

/** Turns `key` on or off in one of the three lists (on when `on` is omitted and it is off). */
export function setChatPref(userId: string, which: Which, key: string, on?: boolean) {
    const cur = readChatPrefs(userId);
    const has = cur[which].includes(key);
    const want = on ?? !has;
    if (want === has) return;
    write(userId, { ...cur, [which]: want ? [...cur[which], key] : cur[which].filter((k) => k !== key) });
}

/** The chat prefs of `userId`, re-rendering when any of them changes (also from another tab). */
export function useChatPrefs(userId: string): ChatPrefs {
    return useSyncExternalStore(
        (cb) => {
            listeners.add(cb);
            const onStorage = (e: StorageEvent) => {
                if (e.key === storageKey(userId)) {
                    cache.delete(userId);
                    cb();
                }
            };
            window.addEventListener("storage", onStorage);
            return () => {
                listeners.delete(cb);
                window.removeEventListener("storage", onStorage);
            };
        },
        () => readChatPrefs(userId)
    );
}
